import ArticleOutlined from "@mui/icons-material/ArticleOutlined";
import AssignmentOutlined from "@mui/icons-material/AssignmentOutlined";
import ChevronRight from "@mui/icons-material/ChevronRight";
import ContactsOutlined from "@mui/icons-material/ContactsOutlined";
import DashboardOutlined from "@mui/icons-material/DashboardOutlined";
import SendOutlined from "@mui/icons-material/SendOutlined";
import TopicOutlined from "@mui/icons-material/TopicOutlined";
import ViewListOutlined from "@mui/icons-material/ViewListOutlined";
import { Box, Button, Stack, Typography } from "@mui/material";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { FeatureFlagOverride } from "generated-graphql/graphql";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useProducts } from "hooks/useProducts";
import { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";

import { getWasteSubmissionsUrl } from "../../Waste/Submissions/navigation";
import { useFacility } from "../useFacility";

interface QuickLinkItem {
  label: string;
  icon: ReactNode;
  to: string;
}

function QuickLinkButton({ label, icon, to }: QuickLinkItem) {
  return (
    <Button
      component={Link}
      to={to}
      fullWidth
      sx={{
        justifyContent: "flex-start",
        textTransform: "uppercase",
        color: "action.active",
        px: 1,
        py: 0.5,
        "& .MuiButton-startIcon": { ml: 0 },
      }}
      startIcon={icon}
      endIcon={<ChevronRight sx={{ ml: "auto" }} />}
    >
      <Box sx={{ flex: 1, textAlign: "left" }}>{label}</Box>
    </Button>
  );
}

export const QuickLinks: React.FC = () => {
  const { tenantId, facilityId } = useParams<{ tenantId: string; facilityId: string }>();
  const { data } = useFacility();
  const isWasteEnabled = data?.facility?.wasteFeatureFlagOverride !== FeatureFlagOverride.Disabled;
  const { featureFlags } = useFeatureFlags();
  const showWasteContacts = featureFlags[FeatureFlagKey.RcraFacilityProfile];
  const { hasProducts } = useProducts();

  const chemicalLinks: QuickLinkItem[] = [
    {
      label: "Inventory",
      icon: <TopicOutlined fontSize="small" />,
      to: `/o/${tenantId}/chemicals/inventory/${facilityId}`,
    },
    {
      label: "Chemical Catalog",
      icon: <ViewListOutlined fontSize="small" />,
      to: `/o/${tenantId}/chemicals/catalog/chemicals`,
    },
    ...(hasProducts
      ? [
          {
            label: "Product Catalog",
            icon: <ViewListOutlined fontSize="small" />,
            to: `/o/${tenantId}/chemicals/products`,
          },
        ]
      : []),
    {
      label: "Chemical Contacts",
      icon: <ContactsOutlined fontSize="small" />,
      to: `/o/${tenantId}/chemicals/contacts/${facilityId}`,
    },
    {
      label: "Chemical Documents",
      icon: <ArticleOutlined fontSize="small" />,
      to: `/o/${tenantId}/chemicals/documents/${facilityId}`,
    },
    {
      label: "Chemical Reports",
      icon: <AssignmentOutlined fontSize="small" />,
      to: `/o/${tenantId}/chemicals/reports?omnisearch=${encodeURIComponent(`facilityId:${facilityId}`)}`,
    },
  ];

  const wasteLinks: QuickLinkItem[] = [
    {
      label: "Waste Profile",
      icon: <DashboardOutlined fontSize="small" />,
      to: `/o/${tenantId}/waste/facilities/${facilityId}`,
    },
    {
      label: "Waste Submissions",
      icon: <SendOutlined fontSize="small" />,
      to: getWasteSubmissionsUrl(
        tenantId ?? "",
        facilityId ? `facilityId:${facilityId}` : undefined
      ),
    },
    ...(showWasteContacts
      ? [
          {
            label: "Waste Contacts",
            icon: <ContactsOutlined fontSize="small" />,
            to: `/o/${tenantId}/waste/contacts/${facilityId}`,
          },
        ]
      : []),
    // TODO: Add when waste documents route exists
    // {
    //   label: "Waste Documents",
    //   icon: <ArticleOutlined fontSize="small" />,
    //   to: `/o/${tenantId}/documents`,
    // },
  ];

  return (
    <Box sx={{ width: "256px", flexShrink: 0 }}>
      <Typography variant="h6" color="text.primary" mb={3}>
        🔗 Quick Links
      </Typography>

      <Stack spacing={3}>
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ lineHeight: 1, display: "block", mb: 1 }}
          >
            Chemicals
          </Typography>
          <Stack spacing={1}>
            {chemicalLinks.map((link) => (
              <QuickLinkButton key={link.label} {...link} />
            ))}
          </Stack>
        </Box>

        {isWasteEnabled && (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1, display: "block", mb: 1 }}
            >
              Waste
            </Typography>
            <Stack spacing={1}>
              {wasteLinks.map((link) => (
                <QuickLinkButton key={link.label} {...link} />
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </Box>
  );
};
